<template>
  <section class="order-packages" dir="rtl" aria-labelledby="order-packages-title">
    <div class="packages-header">
      <div class="packages-heading">
        <span class="packages-kicker">مرحله سوم</span>
        <h2 id="order-packages-title">اطلاعات بسته‌ها</h2>
        <p>مشخصات کالا، وزن، ارزش و ابعاد هر بسته را وارد کنید.</p>
      </div>
      <Button
        type="button"
        icon="pi pi-plus"
        label="افزودن بسته"
        class="packages-add-button"
        @click="addMorePackage"
      />
    </div>

    <div class="package-list">
      <article v-for="(item, index) in OrderPackageItems" :key="index" class="package-item">
        <header class="package-item-header">
          <div class="package-item-title">
            <span class="package-index" aria-hidden="true">{{ String(index + 1).padStart(2, '0') }}</span>
            <div>
              <h3>بسته {{ index + 1 }}</h3>
              <span>مشخصات کالا و بسته‌بندی</span>
            </div>
          </div>
          <div class="package-item-actions">
            <span v-if="OrderPackageItems.length === 1" class="package-required-note">
              <i class="pi pi-lock" aria-hidden="true"></i>
              حداقل یک بسته لازم است
            </span>
            <Button
              v-else
              type="button"
              icon="pi pi-trash"
              label="حذف بسته"
              severity="danger"
              outlined
              class="package-remove-button p-button-sm"
              @click="requestRemoveOrderPackage(index)"
            />
          </div>
        </header>

        <div class="package-item-body">
          <section class="package-overview" aria-label="خلاصه اطلاعات بسته">
            <div class="package-overview-heading">
              <div>
                <span class="package-overview-kicker">مرور سریع</span>
                <h4>خلاصه بسته {{ index + 1 }}</h4>
              </div>
              <span class="package-overview-badge">
                <i class="pi pi-eye" aria-hidden="true"></i>
                نمای کلی
              </span>
            </div>
            <div class="package-summary-grid">
              <div class="package-summary-card package-summary-card--product">
                <span class="package-summary-icon" aria-hidden="true"><i class="pi pi-box"></i></span>
                <div>
                  <small>محصول</small>
                  <strong>{{ displayPackageValue(item.product_name, 'نام محصول') }}</strong>
                </div>
              </div>
              <div class="package-summary-card package-summary-card--quantity">
                <span class="package-summary-icon" aria-hidden="true"><i class="pi pi-list"></i></span>
                <div>
                  <small>تعداد کالا</small>
                  <strong dir="ltr">{{ displayPackageValue(item.count_item) }}</strong>
                </div>
              </div>
              <div class="package-summary-card package-summary-card--count">
                <span class="package-summary-icon" aria-hidden="true"><i class="pi pi-inbox"></i></span>
                <div>
                  <small>تعداد بسته</small>
                  <strong dir="ltr">{{ displayPackageValue(item.count) }}</strong>
                </div>
              </div>
              <div class="package-summary-card package-summary-card--weight">
                <span class="package-summary-icon" aria-hidden="true"><i class="pi pi-chart-bar"></i></span>
                <div>
                  <small>وزن ناخالص</small>
                  <strong dir="ltr">{{ displayPackageValue(item.gross_weight) }}</strong>
                </div>
              </div>
              <div class="package-summary-card package-summary-card--value">
                <span class="package-summary-icon" aria-hidden="true"><i class="pi pi-wallet"></i></span>
                <div>
                  <small>ارزش کالا</small>
                  <strong dir="ltr">{{ displayPackageValue(item.value) }}</strong>
                </div>
              </div>
              <div class="package-summary-card package-summary-card--dimensions">
                <span class="package-summary-icon" aria-hidden="true"><i class="pi pi-arrows-alt"></i></span>
                <div>
                  <small>ابعاد بسته</small>
                  <strong dir="ltr">{{ packageDimensionsLabel(item) }}</strong>
                </div>
              </div>
            </div>
          </section>

          <section class="package-info-section package-info-section--product">
            <div class="package-section-heading">
              <div>
                <span class="package-section-kicker">مشخصات کالا</span>
                <h4>اطلاعات محصول</h4>
              </div>
              <span class="package-section-symbol" aria-hidden="true"><i class="pi pi-box"></i></span>
            </div>
            <div class="package-fields-grid">
              <div class="package-field package-field--wide">
                <label :for="`product_name_${index}`">نام محصول <span class="required-mark">*</span></label>
                <InputText
                  v-model="item.product_name"
                  :id="`product_name_${index}`"
                  required
                  class="w-full"
                  autocomplete="off"
                  placeholder="نام محصول را وارد نمایید"
                />
              </div>
              <div class="package-field">
                <label :for="`hs_code_${index}`">کد HS <span class="required-mark">*</span></label>
                <InputText
                  v-model="item.hs_code"
                  :id="`hs_code_${index}`"
                  required
                  class="w-full"
                  autocomplete="off"
                  placeholder="کد HS"
                />
              </div>
              <div class="package-field">
                <label :for="`count_item_${index}`">تعداد محصول <span class="required-mark">*</span></label>
                <InputText
                  v-model="item.count_item"
                  :id="`count_item_${index}`"
                  required
                  class="w-full"
                  autocomplete="off"
                  placeholder="تعداد محصول"
                />
              </div>
            </div>
          </section>

          <section class="package-info-section package-info-section--weight">
            <div class="package-section-heading">
              <div>
                <span class="package-section-kicker">وزن و تعداد</span>
                <h4>جزئیات بسته‌بندی</h4>
              </div>
              <span class="package-section-symbol" aria-hidden="true"><i class="pi pi-chart-bar"></i></span>
            </div>
            <div class="package-fields-grid">
              <div class="package-field">
                <label :for="`net_weight_${index}`">وزن بدون بسته‌بندی <span class="required-mark">*</span></label>
                <InputText
                  v-model="item.net_weight"
                  :id="`net_weight_${index}`"
                  required
                  class="w-full"
                  autocomplete="off"
                  placeholder="وزن خالص"
                />
              </div>
              <div class="package-field">
                <label :for="`gross_weight_${index}`">وزن با بسته‌بندی <span class="required-mark">*</span></label>
                <InputText
                  v-model="item.gross_weight"
                  :id="`gross_weight_${index}`"
                  required
                  class="w-full"
                  autocomplete="off"
                  placeholder="وزن ناخالص"
                />
              </div>
              <div class="package-field">
                <label :for="`count_${index}`">تعداد بسته <span class="required-mark">*</span></label>
                <InputText
                  v-model="item.count"
                  :id="`count_${index}`"
                  required
                  class="w-full"
                  autocomplete="off"
                  placeholder="تعداد بسته"
                />
              </div>
            </div>
          </section>

          <section class="package-info-section package-info-section--value">
            <div class="package-section-heading">
              <div>
                <span class="package-section-kicker">ارزش کالا</span>
                <h4>ارزش و ارز</h4>
              </div>
              <span class="package-section-symbol" aria-hidden="true"><i class="pi pi-wallet"></i></span>
            </div>
            <div class="package-fields-grid">
              <div class="package-field">
                <label :for="`value_${index}`">ارزش کالا <span class="required-mark">*</span></label>
                <InputNumber
                  v-model="item.value"
                  :id="`value_${index}`"
                  required
                  :min-fraction-digits="0"
                  class="w-full"
                  locale="en-US"
                  autocomplete="off"
                  placeholder="ارزش کالا"
                />
              </div>
              <div class="package-field">
                <label :for="`currency_id_${index}`">ارز</label>
                <SelectInput
                  v-model="item.currency_id"
                  :id="`currency_id_${index}`"
                  placeholder="انتخاب ارز ..."
                  type-primary="Currency"
                  styles="w-full"
                />
              </div>
              <div class="package-field">
                <label :for="`roe_${index}`">نرخ ارز <span class="required-mark">*</span></label>
                <InputNumber
                  v-model="item.roe"
                  :id="`roe_${index}`"
                  required
                  :min-fraction-digits="0"
                  class="w-full"
                  locale="en-US"
                  autocomplete="off"
                  placeholder="نرخ ارز"
                />
              </div>
            </div>
          </section>

          <section class="package-info-section package-info-section--dimensions">
            <div class="package-section-heading">
              <div>
                <span class="package-section-kicker">ابعاد بسته</span>
                <h4>اندازه‌گیری</h4>
              </div>
              <span class="package-section-symbol" aria-hidden="true"><i class="pi pi-arrows-alt"></i></span>
            </div>
            <div class="package-dimension-grid">
              <div class="package-field">
                <label :for="`width_${index}`">عرض <span class="required-mark">*</span></label>
                <InputText
                  v-model="item.order_package_dimension.width"
                  :id="`width_${index}`"
                  required
                  class="w-full"
                  autocomplete="off"
                  placeholder="عرض"
                />
              </div>
              <div class="package-field">
                <label :for="`length_${index}`">طول <span class="required-mark">*</span></label>
                <InputText
                  v-model="item.order_package_dimension.length"
                  :id="`length_${index}`"
                  required
                  class="w-full"
                  autocomplete="off"
                  placeholder="طول"
                />
              </div>
              <div class="package-field">
                <label :for="`height_${index}`">ارتفاع <span class="required-mark">*</span></label>
                <InputText
                  v-model="item.order_package_dimension.height"
                  :id="`height_${index}`"
                  required
                  class="w-full"
                  autocomplete="off"
                  placeholder="ارتفاع"
                />
              </div>
            </div>
          </section>
        </div>
      </article>
    </div>
  </section>

  <DeleteConfirmationDialog
    v-model:visible="deletePackageDialogVisible"
    lang="fa"
    title="حذف بسته"
    message="آیا از حذف این بسته مطمئن هستید؟"
    note="این بسته پس از تأیید از فهرست اطلاعات حذف می‌شود."
    confirm-label="حذف بسته"
    @confirm="confirmRemoveOrderPackage"
  />
</template>

<script setup>
import { onMounted, ref, watch } from 'vue';
import emitter from '@/utils/eventBus';
import SelectInput from '@/components/Global/SelectInput.vue';
import DeleteConfirmationDialog from '@/components/Global/DeleteConfirmationDialog.vue';

const orderPackageSampleItem = () => ({
  product_name: undefined,
  net_weight: undefined,
  gross_weight: undefined,
  count: undefined,
  count_item: undefined,
  value: undefined,
  currency_id: undefined,
  roe: undefined,
  hs_code: undefined,
  order_package_dimension: {
    width: undefined,
    length: undefined,
    height: undefined
  }
});

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  orderId: {
    type: Number,
    required: true
  }
});

const emit = defineEmits(['update:modelValue']);
const OrderPackageItems = ref(props.modelValue);
const deletePackageDialogVisible = ref(false);
const packageToRemove = ref(null);

const displayPackageValue = (value, fallback = '—') => {
  return value === undefined || value === null || value === '' ? fallback : value;
};

const packageDimensionsLabel = (item) => {
  const dimensions = item?.order_package_dimension || {};
  const values = [dimensions.width, dimensions.length, dimensions.height];

  return values.every((value) => value === undefined || value === null || value === '')
    ? '—'
    : values.map((value) => displayPackageValue(value)).join(' × ');
};

const ensurePackageDimension = (item) => {
  if (!item.order_package_dimension) {
    item.order_package_dimension = {
      width: item.width ?? undefined,
      length: item.length ?? undefined,
      height: item.height ?? undefined
    };
  }
};

onMounted(() => {
  OrderPackageItems.value.forEach(ensurePackageDimension);
});

const addMorePackage = () => {
  OrderPackageItems.value.push(orderPackageSampleItem());
};

const requestRemoveOrderPackage = (index) => {
  if (OrderPackageItems.value.length <= 1) {
    emitter.emit('toast', { type: 'error', message: 'اطلاعات حداقل یک بسته الزامیست' });
    return;
  }

  packageToRemove.value = index;
  deletePackageDialogVisible.value = true;
};

const confirmRemoveOrderPackage = () => {
  const index = packageToRemove.value;
  if (index !== null && OrderPackageItems.value.length > 1) {
    OrderPackageItems.value.splice(index, 1);
  }
  packageToRemove.value = null;
  deletePackageDialogVisible.value = false;
};

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== OrderPackageItems.value) {
      OrderPackageItems.value = newValue || [];
      OrderPackageItems.value.forEach(ensurePackageDimension);
    }
  }
);

watch(
  OrderPackageItems,
  (newValue) => emit('update:modelValue', newValue),
  { deep: true }
);
</script>

<style scoped lang="scss">
.order-packages {
  width: 100%;
  min-width: 0;
  color: var(--fm-text-strong);
}

.packages-header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.15rem;
  padding: 1.1rem 1.15rem 1.05rem;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--fm-primary) 16%, var(--fm-border));
  border-radius: var(--fm-radius-lg);
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--fm-primary) 9%, var(--fm-surface)),
    var(--fm-surface) 72%
  );
}

.packages-header::before {
  position: absolute;
  inset-block: 0;
  inset-inline-start: 0;
  width: 0.28rem;
  border-radius: 999px;
  background: linear-gradient(180deg, #2563eb, #8b5cf6 52%, #14b8a6);
  content: '';
}

.packages-heading {
  min-width: 0;
}

.packages-kicker {
  display: block;
  color: var(--fm-primary);
  font-size: 0.76rem;
  font-weight: 800;
}

.package-section-kicker {
  display: block;
  color: var(--section-accent, var(--fm-primary));
  font-size: 0.76rem;
  font-weight: 800;
}

.packages-heading h2 {
  margin: 0.25rem 0 0;
  color: var(--fm-text-strong);
  font-size: 1.25rem;
  line-height: 1.4;
}

.packages-heading p {
  margin: 0.35rem 0 0;
  color: var(--fm-text-muted);
  font-size: 0.84rem;
  line-height: 1.8;
}

.packages-add-button {
  flex: 0 0 auto;
  border-radius: 0.75rem;
  box-shadow: 0 0.5rem 1rem color-mix(in srgb, var(--fm-primary) 16%, transparent);
  white-space: nowrap;
}

.package-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.package-item {
  --package-accent: var(--fm-primary);

  overflow: hidden;
  margin: 0;
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-radius-lg);
  background: var(--fm-surface);
  box-shadow: var(--fm-shadow-sm);
}

.package-item:nth-child(3n + 2) {
  --package-accent: #8b5cf6;
}

.package-item:nth-child(3n + 3) {
  --package-accent: #0f9f9a;
}

.package-item-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.1rem;
  border-top: 0.28rem solid var(--package-accent);
  border-bottom: 1px solid var(--fm-border);
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--package-accent) 9%, var(--fm-surface)),
    var(--fm-surface) 75%
  );
}

.package-item-title {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 0.75rem;
}

.package-index {
  display: grid;
  flex: 0 0 auto;
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid color-mix(in srgb, var(--package-accent) 26%, var(--fm-border));
  border-radius: 0.85rem;
  color: var(--package-accent);
  background: color-mix(in srgb, var(--package-accent) 10%, var(--fm-surface));
  font-size: 0.82rem;
  font-weight: 900;
  place-items: center;
}

.package-item-title h3 {
  margin: 0;
  color: var(--fm-text-strong);
  font-size: 1rem;
  font-weight: 800;
}

.package-item-title div > span {
  display: block;
  margin-top: 0.15rem;
  color: var(--fm-text-muted);
  font-size: 0.76rem;
}

.package-item-actions {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 0.65rem;
}

.package-required-note {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--fm-text-muted);
  font-size: 0.74rem;
  font-weight: 700;
  white-space: nowrap;
}

.package-required-note i {
  color: var(--package-accent);
}

.package-remove-button {
  white-space: nowrap;
}

.package-item-body {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  padding: 1rem;
}

.package-overview {
  position: relative;
  grid-column: 1 / -1;
  overflow: hidden;
  padding: 1rem;
  border: 1px solid color-mix(in srgb, var(--package-accent) 18%, var(--fm-border));
  border-radius: var(--fm-radius-md);
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--package-accent) 8%, var(--fm-surface)),
    var(--fm-surface) 76%
  );
}

.package-overview::after {
  position: absolute;
  inset-block-start: -2.6rem;
  inset-inline-end: -1.8rem;
  width: 7rem;
  height: 7rem;
  border-radius: 50%;
  background: color-mix(in srgb, var(--package-accent) 12%, transparent);
  content: '';
  pointer-events: none;
}

.package-overview-heading {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.85rem;
}

.package-overview-kicker {
  display: block;
  color: var(--package-accent);
  font-size: 0.72rem;
  font-weight: 900;
}

.package-overview-heading h4 {
  margin: 0.18rem 0 0;
  color: var(--fm-text-strong);
  font-size: 0.98rem;
  font-weight: 900;
}

.package-overview-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.6rem;
  border: 1px solid color-mix(in srgb, var(--package-accent) 24%, var(--fm-border));
  border-radius: 999px;
  color: var(--package-accent);
  background: color-mix(in srgb, var(--package-accent) 9%, var(--fm-surface));
  font-size: 0.7rem;
  font-weight: 800;
  white-space: nowrap;
}

.package-summary-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.7rem;
}

.package-summary-card {
  --summary-accent: var(--package-accent);

  display: flex;
  align-items: center;
  min-width: 0;
  gap: 0.65rem;
  padding: 0.72rem;
  border: 1px solid color-mix(in srgb, var(--summary-accent) 16%, var(--fm-border));
  border-radius: 0.85rem;
  background: color-mix(in srgb, var(--summary-accent) 3%, var(--fm-surface));
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.package-summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.45rem 1rem color-mix(in srgb, var(--summary-accent) 11%, transparent);
}

.package-summary-card--product {
  grid-column: span 2;
  --summary-accent: #2563eb;
}

.package-summary-card--quantity {
  --summary-accent: #7c3aed;
}

.package-summary-card--count {
  --summary-accent: #db2777;
}

.package-summary-card--weight {
  --summary-accent: #d97706;
}

.package-summary-card--value {
  --summary-accent: #059669;
}

.package-summary-card--dimensions {
  grid-column: span 2;
  --summary-accent: #0891b2;
}

.package-summary-icon {
  display: grid;
  flex: 0 0 auto;
  width: 2.2rem;
  height: 2.2rem;
  place-items: center;
  border-radius: 0.7rem;
  color: var(--summary-accent);
  background: color-mix(in srgb, var(--summary-accent) 12%, var(--fm-surface));
  font-size: 0.9rem;
}

.package-summary-card div {
  min-width: 0;
}

.package-summary-card small {
  display: block;
  margin-bottom: 0.16rem;
  color: var(--fm-text-muted);
  font-size: 0.7rem;
  font-weight: 700;
}

.package-summary-card strong {
  display: block;
  overflow: hidden;
  color: var(--fm-text-strong);
  font-size: 0.84rem;
  font-weight: 900;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.package-info-section {
  padding: 1rem;
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-radius-md);
  --section-accent: var(--package-accent);
  position: relative;
  overflow: hidden;
  border-inline-start: 0.28rem solid var(--section-accent);
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--section-accent) 7%, var(--fm-surface)),
    var(--fm-surface) 78%
  );
}

.package-info-section::after {
  position: absolute;
  inset-block-start: -2rem;
  inset-inline-end: -2rem;
  width: 5.5rem;
  height: 5.5rem;
  border-radius: 50%;
  background: color-mix(in srgb, var(--section-accent) 10%, transparent);
  content: '';
  pointer-events: none;
}

.package-info-section > * {
  position: relative;
  z-index: 1;
}

.package-info-section--product {
  --section-accent: #2563eb;
}

.package-info-section--weight {
  --section-accent: #d97706;
}

.package-info-section--value {
  --section-accent: #059669;
}

.package-info-section--dimensions {
  grid-column: 1 / -1;
  --section-accent: #0891b2;
}

.package-section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.package-section-heading h4 {
  margin: 0.18rem 0 0;
  color: var(--fm-text-strong);
  font-size: 0.96rem;
  font-weight: 800;
}

.package-section-symbol {
  display: grid;
  flex: 0 0 auto;
  width: 2.25rem;
  height: 2.25rem;
  place-items: center;
  border: 1px solid color-mix(in srgb, var(--section-accent) 18%, var(--fm-border));
  border-radius: 0.75rem;
  color: var(--section-accent);
  background: color-mix(in srgb, var(--section-accent) 11%, var(--fm-surface));
  font-size: 0.95rem;
}

.package-fields-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.package-dimension-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.package-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 0.42rem;
}

.package-field--wide {
  grid-column: 1 / -1;
}

.package-field label {
  display: block;
  color: var(--fm-text-muted);
  font-size: 0.8rem;
  font-weight: 800;
  line-height: 1.6;
}

.required-mark {
  color: var(--fm-danger);
}

.package-field :deep(.p-inputtext),
.package-field :deep(.p-inputnumber),
.package-field :deep(.p-inputnumber-input),
.package-field :deep(.p-dropdown) {
  width: 100%;
}

.package-field :deep(.p-inputtext),
.package-field :deep(.p-inputnumber-input),
.package-field :deep(.p-dropdown) {
  min-height: 2.7rem;
  border-radius: 0.72rem;
  background: var(--fm-surface);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.package-field :deep(.p-inputtext:focus),
.package-field :deep(.p-inputnumber-input:focus),
.package-field :deep(.p-dropdown.p-focus) {
  border-color: var(--section-accent);
  box-shadow: 0 0 0 0.18rem color-mix(in srgb, var(--section-accent) 15%, transparent);
}

@media (max-width: 980px) {
  .package-item-body {
    grid-template-columns: 1fr;
  }

  .package-info-section {
    grid-column: 1 / -1;
  }

  .package-fields-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .package-field--wide {
    grid-column: span 2;
  }
}

@media (max-width: 760px) {
  .package-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .package-summary-card--product,
  .package-summary-card--dimensions {
    grid-column: span 2;
  }

  .package-fields-grid,
  .package-dimension-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .package-field--wide {
    grid-column: 1 / -1;
  }
}

@media (max-width: 620px) {
  .packages-header {
    align-items: stretch;
    flex-direction: column;
  }

  .packages-add-button {
    width: 100%;
  }

  .package-item-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .package-item-actions,
  .package-remove-button {
    width: 100%;
  }

  .package-required-note {
    width: 100%;
  }

  .package-summary-grid,
  .package-fields-grid,
  .package-dimension-grid {
    grid-template-columns: 1fr;
  }

  .package-summary-card--product,
  .package-summary-card--dimensions,
  .package-field--wide {
    grid-column: auto;
  }
}

@media (max-width: 420px) {
  .package-item-body,
  .package-info-section {
    padding: 0.8rem;
  }

  .package-index {
    width: 2.25rem;
    height: 2.25rem;
  }
}
</style>
